<script setup lang="ts">
import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'

type ProductCardSummaryProps = {
  product: {
    productId: string
    productName: string
    price?: number
    tieredPrice?: ShopperProductsTypes.ProductPriceTable[] | ConstructorProductPrice
    image?: {
      url: string
      alt: string
    }
    url?: string
    promotions?: Pick<ShopperProductsTypes.ProductPromotion, 'promotionId'>[]
    productPromotions?: Pick<ShopperProductsTypes.ProductPromotion, 'promotionId'>[]
  }
}
const props = defineProps<ProductCardSummaryProps>()
const { locale } = useI18n()
const url = ref()

watchEffect(() => {
  url.value = getProductUrl({
    productId: props.product.productId,
    productName: props.product.productName,
    locale: locale.value,
    slugUrl: props.product.url,
  })
})
</script>

<template>
  <article class="product-card--summary" :id="product.productId">
    <ProductCardImage class="product-card__image" v-if="product.image" :image="product.image" :product-url="url" />
    <div class="product-card--summary-wrapper">
      <ProductCardTitle :productUrl="url" :productName="product.productName" />
      <div class="product-card--summary-details">
        <PromotionList
          v-if="product.promotions?.length || product.productPromotions?.length"
          :promotions="product.promotions || product.productPromotions"
          class="product-card__promotions"
        />
        <Pricing :tiered-prices="product.tieredPrice" :price="product.price" class="product-card__pricing" />
      </div>
      <slot />
    </div>
  </article>
</template>

<style lang="postcss">
.product-card--summary {
  display: flex;
  gap: var(--space-30);

  .product-card__image {
    flex: 0 0 6rem;
    align-self: flex-start; /* Keeps the aspect ratio when the card's content is taller than the image */
    width: 6rem; /* 96px */
    aspect-ratio: var(--aspect-ratio-product);
    overflow: hidden;
    background: var(--surface-light-20);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .product-card--summary-wrapper {
    width: 100%;
  }

  .product-card--summary-details {
    display: flex;
    justify-content: space-between;
  }

  .product-card__promotions {
    margin-top: var(--space-30);
  }

  .product-card__pricing {
    font-size: var(--text-large);
    font-weight: bold;
    margin-left: auto;
    margin-top: var(--space-30);

    .pricing__double {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }

    .pricing__double--amount {
      margin-right: 0;
      margin-bottom: var(--space-50);
    }

    .pricing__double--description,
    .pricing__double--strike {
      font-weight: normal;
      margin: 0;
    }
  }
}
</style>
